import type { WorkflowStep } from '../services/workflowService';
import {
  canonicalStepType,
  CANONICAL_STEP_TYPES,
  getApplicableActionsForStepType,
  getStepTypeLabel,
  isApplicableActionForStepType,
  isArchiveStepType,
  isEditStepType,
  isKnownWorkflowAction,
  isKnownWorkflowStepType,
  isPublishStepType,
  isRequiredStepType,
  isReviewPhaseStepType,
  isSignatureStepType,
  isSingleInstanceStepType,
  WORKFLOW_ACTION_CODES_ALL,
  WORKFLOW_STEP_TYPES_ALL,
} from './workflowConstants';
import { getIncoherentActionsForStep } from './workflowActionCoherence';

export function validateWorkflowDefinition(steps: WorkflowStep[] | null | undefined): string[] {
  const errors: string[] = [];
  if (steps == null || steps.length === 0) {
    errors.push('O workflow deve possuir pelo menos uma etapa do tipo Edição.');
    return errors;
  }

  const ordered = [...steps].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return (a.id || '').localeCompare(b.id || '');
  });

  const duplicateOrders = ordered
    .reduce((acc, s) => {
      acc.set(s.order, (acc.get(s.order) ?? 0) + 1);
      return acc;
    }, new Map<number, number>());
  const dupKeys = Array.from(duplicateOrders.entries())
    .filter(([, c]) => c > 1)
    .map(([k]) => k);
  if (dupKeys.length > 0) {
    errors.push(
      `Cada etapa precisa de um valor de ordem único; ordens repetidas: ${dupKeys.sort((a, b) => a - b).join(', ')}.`
    );
  }

  let unknownType = false;
  for (const step of ordered) {
    if (!step.type?.trim() || !isKnownWorkflowStepType(step.type)) {
      unknownType = true;
      const label = !step.name?.trim() ? step.id : step.name;
      errors.push(
        `Tipo de etapa inválido na etapa '${label}': use um dos tipos conhecidos (${WORKFLOW_STEP_TYPES_ALL.join(', ')}).`
      );
      continue;
    }
    appendAllowedActionErrors(step, errors);
  }

  if (unknownType) return errors;

  for (const type of CANONICAL_STEP_TYPES) {
    if (isRequiredStepType(type) && !ordered.some(s => canonicalStepType(s.type) === type)) {
      errors.push(`O workflow deve possuir ao menos uma etapa do tipo ${getStepTypeLabel(type)}.`);
    }
  }

  let seenNonEdit = false;
  for (const s of ordered) {
    if (isEditStepType(s.type)) {
      if (seenNonEdit) {
        errors.push(
          'As etapas de edição devem ficar todas no início do fluxo, em sequência contígua; não pode haver etapa de edição depois de outros tipos.'
        );
        break;
      }
    } else {
      seenNonEdit = true;
    }
  }

  for (const type of CANONICAL_STEP_TYPES) {
    if (isSingleInstanceStepType(type) && ordered.filter(s => canonicalStepType(s.type) === type).length > 1) {
      errors.push(`O workflow pode ter no máximo uma etapa do tipo ${getStepTypeLabel(type)}.`);
    }
  }

  const publishIndex = ordered.findIndex(s => isPublishStepType(s.type));
  const signatureIndex = ordered.findIndex(s => isSignatureStepType(s.type));

  if (signatureIndex >= 0) {
    if (publishIndex < 0) {
      errors.push(
        'Se existir etapa de assinatura, o fluxo deve incluir Publicação imediatamente após essa etapa.'
      );
    } else if (publishIndex !== signatureIndex + 1) {
      errors.push(
        'A etapa de assinatura, quando existir, deve ser imediatamente anterior à etapa Publicação, sem nenhuma etapa entre elas.'
      );
    }
  }

  let reviewApproveBarrier: number | null = null;
  if (signatureIndex >= 0) reviewApproveBarrier = signatureIndex;
  else if (publishIndex >= 0) reviewApproveBarrier = publishIndex;

  if (reviewApproveBarrier != null) {
    for (let i = 0; i < ordered.length; i++) {
      if (!isReviewPhaseStepType(ordered[i].type)) continue;
      if (i >= reviewApproveBarrier) {
        errors.push(
          'Etapas de Revisão ou Aprovação devem ocorrer depois de todas as edições e antes de Assinatura Digital ou Publicação.'
        );
        break;
      }
    }
  }

  if (publishIndex >= 0) {
    for (let i = publishIndex + 1; i < ordered.length; i++) {
      if (isReviewPhaseStepType(ordered[i].type) || isSignatureStepType(ordered[i].type)) {
        errors.push(
          'Não pode haver etapas de Revisão, Aprovação ou assinatura depois da etapa Publicação.'
        );
        break;
      }
      if (!isArchiveStepType(ordered[i].type)) {
        errors.push('Depois da etapa Publicação só é permitida a etapa Arquivamento, se existir.');
        break;
      }
    }
  }

  for (let i = 0; i < ordered.length; i++) {
    if (!isArchiveStepType(ordered[i].type)) continue;
    if (i !== ordered.length - 1) {
      errors.push('A etapa Arquivamento (Archive), quando existir, deve ser a última do workflow.');
      break;
    }
  }

  for (let i = 0; i < ordered.length; i++) {
    const incoherent = getIncoherentActionsForStep(ordered[i], i, ordered);
    const label = !ordered[i].name?.trim() ? ordered[i].id : ordered[i].name;
    for (const item of incoherent) {
      errors.push(`Ação '${item.action}' na etapa '${label}': ${item.reason}`);
    }
  }

  return errors;
}

function appendAllowedActionErrors(step: WorkflowStep, errors: string[]): void {
  const actions = step.allowedActions;
  if (actions == null || actions.length === 0) return;

  const label = !step.name?.trim() ? step.id : step.name;
  for (const action of actions) {
    if (!action?.trim()) continue;
    if (!isKnownWorkflowAction(action)) {
      errors.push(
        `Ação '${action}' na etapa '${label}' não pertence ao catálogo de ações do workflow. Códigos válidos: ${WORKFLOW_ACTION_CODES_ALL.join(', ')}.`
      );
      continue;
    }
    if (!isApplicableActionForStepType(step.type, action)) {
      const hint = getApplicableActionsForStepType(step.type).join(', ');
      errors.push(
        `Ação '${action}' não é permitida para o tipo '${step.type}' na etapa '${label}'. Ações válidas: ${hint.length > 0 ? hint : '(nenhuma)'}.`
      );
    }
  }
}
